import { gql } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { NavBarItemType, type NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const createCardInWorkspaceMutation = gql`
	mutation createCardInWorkspace(
		$section: NavbarSection!
		$name: String!
		$cardId: String
		$parentFolderKey: String
		$beforeKey: String
		$afterKey: String
	) {
		navbar {
			createCard(
				section: $section
				name: $name
				cardId: $cardId
				parentFolderKey: $parentFolderKey
				beforeKey: $beforeKey
				afterKey: $afterKey
			) {
				key
				name
				parentKey
				rank
			}
		}
	}
`;

export interface CreateCardInWorkspaceParams {
	section: NavbarSection;
	name: string;
	cardId: string;
	parentFolderKey?: string | null;
	beforeKey?: string | null;
	afterKey?: string | null;
}

export function useCreateCardInWorkspace() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			name,
			cardId,
			parentFolderKey,
			beforeKey,
			afterKey,
		}: CreateCardInWorkspaceParams) => {
			const response = await client.mutate({
				mutation: createCardInWorkspaceMutation,
				variables: {
					section,
					name,
					cardId,
					parentFolderKey: parentFolderKey ?? undefined,
					beforeKey: beforeKey ?? undefined,
					afterKey: afterKey ?? undefined,
				},
			});
			return response.data?.navbar?.createCard ?? null;
		},
		onMutate: async (variables) => {
			const queryKey = navbarBranchQueryKeys.branch(
				variables.section,
				variables.parentFolderKey,
			);
			await queryClient.cancelQueries({ queryKey });
			const previousItems =
				queryClient.getQueryData<NavbarBranchItem[]>(queryKey);

			const tempKey = variables.cardId;
			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
				const placeholder: NavbarBranchItem = {
					key: tempKey,
					name: variables.name,
					type: NavBarItemType.Card,
					parentKey: variables.parentFolderKey ?? null,
					rank: "",
					hasChildren: false,
				};
				return [placeholder, ...(old ?? [])];
			});

			return { previousItems, queryKey, tempKey };
		},
		onSuccess: (data, variables, context) => {
			if (!data || !context?.tempKey) {
				return;
			}
			const queryKey = navbarBranchQueryKeys.branch(
				variables.section,
				variables.parentFolderKey,
			);
			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
				if (!old) {
					return old;
				}
				const realItem: NavbarBranchItem = {
					key: data.key,
					name: data.name,
					type: NavBarItemType.Card,
					parentKey: data.parentKey ?? null,
					rank: data.rank,
					hasChildren: false,
				};
				return old.map((i) => (i.key === context.tempKey ? realItem : i));
			});
		},
		onError: (error, _variables, context) => {
			if (context?.queryKey) {
				queryClient.setQueryData(context.queryKey, context.previousItems);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.branch(
					variables.section,
					variables.parentFolderKey,
				),
			});
		},
	});
}
